import React, { createContext, useContext, useState, useEffect } from 'react';
import { CATEGORIES, MEDIA } from '../config/api.config';
import imagePreloader from '../utils/imagePreloader';

const CategoryContext = createContext();

export const useCategory = () => {
  const context = useContext(CategoryContext);
  if (!context) {
    throw new Error('useCategory must be used within a CategoryProvider');
  }
  return context;
};

export const CategoryProvider = ({ children }) => {
  const [categories, setCategories] = useState([]);
  const [categoryIcon, setCategoryIcon] = useState("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2Y3ZjdmNyIvPjx0ZXh0IHg9IjUwIiB5PSI1MCIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+SW1hZ2U8L3RleHQ+PC9zdmc+");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Image cache to prevent duplicate requests
  const imageCache = new Set();

  const preloadImage = (src) => {
    return imagePreloader.preloadImage(src);
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(CATEGORIES.ALL);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log("Categories data:", data);
      
      // Handle different response formats
      if (Array.isArray(data)) {
        setCategories(data);
      } else if (data.data && Array.isArray(data.data)) {
        setCategories(data.data);
      } else {
        console.warn("Unexpected categories data format:", data);
        setCategories([]);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setError(error.message);
      setCategories([]);
    }
  };

  const fetchCategoryIcon = async () => {
    try {
      // Try to fetch categories from the new API first
      const response = await fetch(MEDIA.CATEGORIES);
      if (response.ok) {
        const data = await response.json();
        console.log("Category icon data:", data);
        const categories = data?.data || [];
        
        // Use the first category's icon if available
        if (categories.length > 0 && categories[0].icon) {
          setCategoryIcon(categories[0].icon);
          return;
        }
      }
    } catch (error) {
      console.error("Error fetching category icon:", error);
    }
    
    // Keep the default fallback if API fails
    setCategoryIcon("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2Y3ZjdmNyIvPjx0ZXh0IHg9IjUwIiB5PSI1MCIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+SW1hZ2U8L3RleHQ+PC9zdmc+");
  };

  const initializeData = async () => {
    if (isInitialized) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch both categories and icon in parallel
      await Promise.all([
        fetchCategories(),
        fetchCategoryIcon()
      ]);
      
      // Note: Removed preloading to prevent multiple requests
      // Images will be loaded on-demand by components
      
    } catch (error) {
      console.error("Error initializing category data:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
      setIsInitialized(true);
    }
  };

  useEffect(() => {
    initializeData();
  }, []);

  const value = {
    categories,
    categoryIcon,
    isLoading,
    error,
    isInitialized,
    preloadImage,
    refreshData: initializeData
  };

  return (
    <CategoryContext.Provider value={value}>
      {children}
    </CategoryContext.Provider>
  );
};
